import { useState, useEffect } from 'react';
import api from '../axiosConfig';
import {
  Container,
  Typography,
  Button,
  Link,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';

const Dashboard = () => {
  const [data, setData] = useState({ sessions: [], clients: [], activeSession: null });
  const [newSession, setNewSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    clientId: 1,
    presenter: '',
    location: '',
    eventDate: ''
  });

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const fetchData = async () => {
    try {
      const response = await api.get('/dashboard/sessions');
      setData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const createSession = async () => {
    try {
      const response = await api.post('/dashboard/createSession', formData);

      setNewSession(response.data.session);
      fetchData();
      handleClose();
    } catch (error) {
      console.error('Error creating session:', error);
    }
  };

  const setActiveSession = async (sessionId) => {
    try {
      await api.post('/dashboard/activeSession', { sessionId });
      fetchData();
    } catch (error) {
      console.error('Error setting active session:', error);
    }
  };

  const sortedSessions = () => {
    const sessions = [...data.sessions];
    const activeSession = data.activeSession;

    if (activeSession) {
      const activeIndex = sessions.findIndex((session) => session.id === activeSession);
      if (activeIndex > -1) {
        const [activeSession] = sessions.splice(activeIndex, 1);
        sessions.unshift(activeSession);
      }
    }

    return sessions;
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Sessions Dashboard
      </Typography>

      <Button variant="contained" color="primary" onClick={handleOpen}>
        Create Session
      </Button>

      {newSession && (
        <Typography variant="h6" style={{ marginTop: '20px' }}>
          New Session Created: {newSession.id}
        </Typography>
      )}

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Create New Session</DialogTitle>
        <DialogContent>
          <FormControl fullWidth style={{ marginBottom: '20px' }}>
            <InputLabel id="client-select-label">Client</InputLabel>
            <Select
              labelId="client-select-label"
              value={formData.clientId}
              onChange={handleInputChange}
              name="clientId"
            >
              {data.clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            margin="normal"
            label="Presenter"
            name="presenter"
            value={formData.presenter}
            onChange={handleInputChange}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Location"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Date"
            type="date"
            name="eventDate"
            value={formData.eventDate}
            onChange={handleInputChange}
            InputLabelProps={{
              shrink: true
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={createSession} color="primary">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Grid2 container spacing={3} sx={{ marginTop: '20px' }}>
        {sortedSessions().map((session) => {
          const client = data.clients.find((c) => c.id === session.clientId);
          const isActive = data.activeSession && data.activeSession === session.id;

          return (
            <Grid2
              item
              xs={12}
              sm={6}
              md={4}
              key={session.id}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignContent: 'center'
              }}
            >
              <Card
                sx={{
                  width: '510px',
                  backgroundColor: isActive ? 'lightblue' : 'inherit'
                }}
              >
                <CardContent>
                  <Typography variant="h6">Session ID: {session.id}</Typography>
                  <Typography variant="body1">Client: {client ? client.name : 'Unknown'}</Typography>
                  <Typography variant="body1">Presenter: {session.presenter || 'N/A'}</Typography>
                  <Typography variant="body1">Location: {session.location || 'N/A'}</Typography>
                  <Typography variant="body1">Date: {session.eventDate || 'N/A'}</Typography>
                </CardContent>
                <CardActions>
                  <Link href={`/attendee/${session.id}`} target="_blank">
                    <Button variant="outlined">Attendee</Button>
                  </Link>
                  <Link href={`/monitor/${session.id}`} target="_blank">
                    <Button variant="outlined">Monitor</Button>
                  </Link>
                  <Link href={`/moderator/${session.id}`} target="_blank">
                    <Button variant="outlined">Moderator</Button>
                  </Link>
                  <Link href={`/presenter/${session.id}`} target="_blank">
                    <Button variant="outlined">Presenter</Button>
                  </Link>
                  {!isActive && (
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ backgroundColor: '#9C27B0' }}
                      onClick={() => setActiveSession(session.id)}
                    >
                      Set as Active
                    </Button>
                  )}
                  {isActive && <Typography variant="subtitle2">Active Session</Typography>}
                </CardActions>
              </Card>
            </Grid2>
          );
        })}
      </Grid2>
    </Container>
  );
};

export default Dashboard;
